import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import {DateCalendar, LocalizationProvider, PickersDay} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import type {PickersCalendarHeaderProps} from '@mui/x-date-pickers/PickersCalendarHeader';
import type {PickersDayProps} from '@mui/x-date-pickers/PickersDay';
import type {CommitInfo} from '_logics/LFCSourceUtil';
import dayjs, {Dayjs} from 'dayjs';
import 'dayjs/locale/ja';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  findAdjacentChangedMonth,
  getChangedDaysInMonth,
  getChangedMonths,
  getCommitsOnDate
} from './historyCalendarSelection';

export type CommitHistoryCalendarPickerProps = {
  label: string;
  commits: readonly CommitInfo[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  locale: 'ja' | 'en';
  olderMonthLabel: string;
  newerMonthLabel: string;
  loadingText: string;
  hasMore?: boolean;
  onLoadMore?: () => void | Promise<void>;
  loadMoreLoading?: boolean;
  disabled?: boolean;
  disabledIndices?: readonly number[];
  isCommitSelectable?: (index: number, commit: CommitInfo) => boolean;
};

type PendingOlderNavigation = {
  sourceMonth: string;
  requestedFingerprint: string;
  action: 'complete-month' | 'navigate';
};

const toMonthKey = (value: Dayjs): string => value.format('YYYY-MM');

const monthStart = (monthKey: string): Dayjs => dayjs(`${monthKey}-01`);

const getDisplayCommitMessage = (message: string): string =>
  message
    .split(/\r?\n/)
    .map(line =>
      line
        .replace(
          /\b\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?\s*(?:UTC|GMT)?\b/gi,
          ''
        )
        .trim()
    )
    .filter(Boolean)
    .join('\n');

const CommitHistoryCalendarPicker = memo(
  ({
    label,
    commits,
    selectedIndex,
    onSelect,
    locale,
    olderMonthLabel,
    newerMonthLabel,
    loadingText,
    hasMore = false,
    onLoadMore,
    loadMoreLoading = false,
    disabled = false,
    disabledIndices = [],
    isCommitSelectable
  }: CommitHistoryCalendarPickerProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [currentMonth, setCurrentMonth] = useState<string | null>(null);
    const [selectedDay, setSelectedDay] = useState<Dayjs | null>(null);
    const [pendingOlderNavigation, setPendingOlderNavigation] =
      useState<PendingOlderNavigation | null>(null);
    const loadMoreRef = useRef(onLoadMore);
    const wasLoadMoreLoadingRef = useRef(loadMoreLoading);

    useEffect(() => {
      loadMoreRef.current = onLoadMore;
    }, [onLoadMore]);

    const changedMonths = useMemo(() => getChangedMonths(commits), [commits]);
    const lastCommit = commits.length > 0 ? commits[commits.length - 1] : undefined;
    const commitsFingerprint = `${commits.length}:${lastCommit?.commitId ?? ''}:${lastCommit?.date ?? ''}`;
    const selectedCommit =
      selectedIndex !== null && selectedIndex >= 0 && selectedIndex < commits.length
        ? commits[selectedIndex]
        : null;

    const selectedCommitDate = useMemo(() => {
      if (!selectedCommit) return null;
      const value = dayjs(selectedCommit.date);
      return value.isValid() ? value : null;
    }, [selectedCommit]);

    const changedDays = useMemo(
      () => (currentMonth ? getChangedDaysInMonth(commits, currentMonth) : new Set<number>()),
      [commits, currentMonth]
    );

    const commitsOnSelectedDay = useMemo(() => {
      if (!selectedDay) return [];
      return getCommitsOnDate(commits, selectedDay.toDate())
        .map(commit => ({commit, index: commits.indexOf(commit)}))
        .filter(item => item.index >= 0);
    }, [commits, selectedDay]);

    const isIndexSelectable = useCallback(
      (index: number, commit: CommitInfo): boolean =>
        !disabledIndices.includes(index) && (isCommitSelectable?.(index, commit) ?? true),
      [disabledIndices, isCommitSelectable]
    );

    const openPicker = (event: React.MouseEvent<HTMLButtonElement>) => {
      const initialMonth = selectedCommitDate
        ? toMonthKey(selectedCommitDate)
        : (changedMonths[0] ?? null);
      setCurrentMonth(initialMonth);
      setSelectedDay(selectedCommitDate);
      setAnchorEl(event.currentTarget);
    };

    const closePicker = () => {
      setAnchorEl(null);
      setPendingOlderNavigation(null);
    };

    const moveToMonth = useCallback((monthKey: string) => {
      setCurrentMonth(monthKey);
      setSelectedDay(null);
    }, []);

    const requestOlderHistory = useCallback(
      (sourceMonth: string, action: PendingOlderNavigation['action']) => {
        if (!hasMore || !loadMoreRef.current || loadMoreLoading) return;
        setPendingOlderNavigation({
          sourceMonth,
          requestedFingerprint: commitsFingerprint,
          action
        });
        void loadMoreRef.current();
      },
      [commitsFingerprint, hasMore, loadMoreLoading]
    );

    useEffect(() => {
      if (!pendingOlderNavigation || loadMoreLoading) return;

      const olderMonth = findAdjacentChangedMonth(
        changedMonths,
        pendingOlderNavigation.sourceMonth,
        'older'
      );
      if (olderMonth) {
        if (pendingOlderNavigation.action === 'navigate') {
          moveToMonth(olderMonth);
        }
        setPendingOlderNavigation(null);
        return;
      }

      if (!hasMore) {
        setPendingOlderNavigation(null);
        return;
      }

      if (
        commitsFingerprint !== pendingOlderNavigation.requestedFingerprint &&
        loadMoreRef.current
      ) {
        setPendingOlderNavigation(previous =>
          previous ? {...previous, requestedFingerprint: commitsFingerprint} : previous
        );
        void loadMoreRef.current();
      }
    }, [
      changedMonths,
      commitsFingerprint,
      hasMore,
      loadMoreLoading,
      moveToMonth,
      pendingOlderNavigation
    ]);

    // API失敗などで履歴が増えずに読込だけ終了した場合は、再試行できる状態へ戻す。
    useEffect(() => {
      const loadFinished = wasLoadMoreLoadingRef.current && !loadMoreLoading;
      wasLoadMoreLoadingRef.current = loadMoreLoading;

      if (
        loadFinished &&
        pendingOlderNavigation &&
        commitsFingerprint === pendingOlderNavigation.requestedFingerprint
      ) {
        setPendingOlderNavigation(null);
        setAnchorEl(null);
      }
    }, [commitsFingerprint, loadMoreLoading, pendingOlderNavigation]);

    // 最古の読込済み月は、次の変更月が見つかるまでその月の履歴が完了したと判断できない。
    useEffect(() => {
      if (
        !anchorEl ||
        !currentMonth ||
        pendingOlderNavigation ||
        loadMoreLoading ||
        !hasMore ||
        changedMonths[changedMonths.length - 1] !== currentMonth
      ) {
        return;
      }

      requestOlderHistory(currentMonth, 'complete-month');
    }, [
      anchorEl,
      changedMonths,
      currentMonth,
      hasMore,
      loadMoreLoading,
      pendingOlderNavigation,
      requestOlderHistory
    ]);

    const calendarValue = useMemo(() => {
      if (!currentMonth) return null;
      if (selectedDay && toMonthKey(selectedDay) === currentMonth) return selectedDay;
      return monthStart(currentMonth);
    }, [currentMonth, selectedDay]);

    const changeDay = (value: Dayjs | null) => {
      if (value && changedDays.has(value.date())) {
        setSelectedDay(value);
      }
    };

    const ChangedDay = useCallback(
      (props: PickersDayProps<Dayjs>) => {
        const hasChanges =
          currentMonth === toMonthKey(props.day) && changedDays.has(props.day.date());
        const isSelected = Boolean(selectedDay?.isSame(props.day, 'day'));

        return (
          <PickersDay
            {...props}
            disabled={props.disabled || !hasChanges}
            selected={isSelected}
            sx={{
              ...props.sx,
              ...(!isSelected && hasChanges
                ? {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 700,
                    '&:hover, &:focus': {bgcolor: 'primary.dark'}
                  }
                : {})
            }}
          />
        );
      },
      [changedDays, currentMonth, selectedDay]
    );

    const CalendarHeader = useCallback(
      (props: PickersCalendarHeaderProps<Dayjs>) => {
        const shownMonth = toMonthKey(props.currentMonth);
        const olderMonth = findAdjacentChangedMonth(changedMonths, shownMonth, 'older');
        const newerMonth = findAdjacentChangedMonth(changedMonths, shownMonth, 'newer');
        const isLoadingOlder = pendingOlderNavigation !== null || loadMoreLoading;
        const displayMonth =
          locale === 'ja'
            ? `${props.currentMonth.year()}年${props.currentMonth.month() + 1}月`
            : props.currentMonth.locale('en').format('MMMM YYYY');

        const handleMove = (direction: 'older' | 'newer') => {
          const adjacent = direction === 'older' ? olderMonth : newerMonth;
          if (adjacent) {
            props.onMonthChange(monthStart(adjacent), direction === 'older' ? 'right' : 'left');
            moveToMonth(adjacent);
          } else if (direction === 'older') {
            requestOlderHistory(shownMonth, 'navigate');
          }
        };

        return (
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1}}>
            <IconButton
              aria-label={olderMonthLabel}
              onClick={() => handleMove('older')}
              disabled={isLoadingOlder || (!olderMonth && (!hasMore || !onLoadMore))}
              size="small"
            >
              {isLoadingOlder ? <CircularProgress size={20} /> : <ChevronLeftIcon />}
            </IconButton>
            <Typography data-testid="changed-month-label" fontWeight={600}>
              {displayMonth}
            </Typography>
            <IconButton
              aria-label={newerMonthLabel}
              onClick={() => handleMove('newer')}
              disabled={!newerMonth || isLoadingOlder}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        );
      },
      [
        changedMonths,
        hasMore,
        loadMoreLoading,
        locale,
        moveToMonth,
        newerMonthLabel,
        olderMonthLabel,
        onLoadMore,
        pendingOlderNavigation,
        requestOlderHistory
      ]
    );

    const chooseCommit = (index: number, commit: CommitInfo) => {
      if (!isIndexSelectable(index, commit)) return;
      onSelect(index);
      closePicker();
    };

    const currentMonthLoading =
      pendingOlderNavigation?.sourceMonth === currentMonth ||
      (loadMoreLoading && changedMonths[changedMonths.length - 1] === currentMonth);

    return (
      <>
        <Tooltip title={label}>
          <Box component="span" sx={{display: 'block'}}>
            <Button
              aria-label={label}
              variant="outlined"
              size="small"
              onClick={openPicker}
              disabled={disabled || changedMonths.length === 0}
              endIcon={<CalendarMonthIcon />}
              sx={{
                width: '100%',
                minWidth: 0,
                justifyContent: 'space-between',
                textTransform: 'none',
                bgcolor: theme.palette.background.paper,
                '&:hover': {bgcolor: theme.palette.background.default}
              }}
            >
              <Typography component="span" variant="body2" noWrap>
                {selectedCommitDate?.format('YYYY/MM/DD HH:mm:ss') ?? '—'}
              </Typography>
            </Button>
          </Box>
        </Tooltip>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={closePicker}
          anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
          slotProps={{
            paper: {
              sx: {width: 320, maxWidth: 'calc(100vw - 32px)'}
            }
          }}
        >
          {currentMonth && (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
              <DateCalendar
                value={calendarValue}
                onChange={changeDay}
                loading={currentMonthLoading}
                renderLoading={() => (
                  <Box sx={{height: 248, display: 'grid', placeItems: 'center'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <CircularProgress size={20} />
                      <Typography variant="caption" color="text.secondary">
                        {loadingText}
                      </Typography>
                    </Box>
                  </Box>
                )}
                shouldDisableDate={date =>
                  toMonthKey(date) !== currentMonth || !changedDays.has(date.date())
                }
                views={['day']}
                disableHighlightToday
                disabled={Boolean(pendingOlderNavigation) || loadMoreLoading}
                showDaysOutsideCurrentMonth={false}
                slots={{calendarHeader: CalendarHeader, day: ChangedDay}}
                sx={{width: 320, maxHeight: 340}}
              />
            </LocalizationProvider>
          )}
          {(loadMoreLoading || pendingOlderNavigation) && !currentMonthLoading && (
            <Box
              sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, pb: 1}}
            >
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {loadingText}
              </Typography>
            </Box>
          )}
          {selectedDay && commitsOnSelectedDay.length > 0 && (
            <Box sx={{borderTop: 1, borderColor: 'divider', maxHeight: 360, overflow: 'auto'}}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{display: 'block', px: 2, pt: 1}}
              >
                {selectedDay.format('YYYY/MM/DD')}
              </Typography>
              <List dense disablePadding>
                {commitsOnSelectedDay.map(({commit, index}) => {
                  const selectable = isIndexSelectable(index, commit);
                  return (
                    <ListItemButton
                      key={`${commit.commitId}-${index}`}
                      selected={index === selectedIndex}
                      disabled={!selectable}
                      onClick={() => chooseCommit(index, commit)}
                      sx={{alignItems: 'flex-start'}}
                    >
                      <ListItemText
                        sx={{minWidth: 0, mr: 1}}
                        primary={dayjs(commit.date).format('HH:mm:ss')}
                        secondary={getDisplayCommitMessage(commit.message) || undefined}
                        secondaryTypographyProps={{
                          sx: {display: 'block', whiteSpace: 'normal', overflowWrap: 'anywhere'}
                        }}
                      />
                      {commit.changeType && (
                        <Chip label={commit.changeType} size="small" sx={{flexShrink: 0}} />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          )}
        </Popover>
      </>
    );
  }
);

CommitHistoryCalendarPicker.displayName = 'CommitHistoryCalendarPicker';

export default CommitHistoryCalendarPicker;
